// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';

import { updateStateStatus } from '../utils/utils';

import {
  fetchUserStudios,
  fetchStudioByUuid,
  addSceneToStudio,
  fetchStudiosMembers,
  removeStudioMember,
  addStudioMember,
  removeSceneFromStudio,
  addEquipmentToStudio,
  updateEquipmentFromStudio,
  deleteEquipmentFromStudio,
} from './studioActions';

import { updateStudio, deleteStudio } from '../companies/companiesActions';

import {
  studioStateChangedState,
  studioConditionChangedState,
  sceneInStudioChangedState,
  userStudiosState,
  studioByUuidState,
  updatedStudioState,
  deleteStudioState,
  addEquipmentState,
  updateEquipmentState,
  deleteEquipmentState,
  addSceneToStudioState,
  deleteSceneFromStudioState,
  studiosMembersState,
  addStudioMemberState,
  deleteStudioMemberState,
} from './studioReducers';

const initialState = {
  studios: [],
  studio: {
    studio_scenes: [],
    studio_equipment: [],
    studioState: 'Disconnected', // 'Connected' | 'Disconnected'
    members: [],
    studioCondition: 'Unknown', // 'Unknown' | 'On Air' | 'In Rehearsal' | 'Off Air'
  },
  status: { studio: 'idle', studios: 'idle' }, // possible status state : 'idle' | 'loading' | 'succeeded' | 'failed'
  error: { studio: null, studios: null, studioEquipment: null },
};

const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    clearError: state => {
      state.error = initialState.error;
    },
    clearStudio: state => {
      state.studio = initialState.studio;
      state.status.studio = 'idle';
      state.error.studio = null;
      state.error.studioEquipment = null;
    },
    studioStateChanged: (state, action) => {
      studioStateChangedState(state, action.payload);
    },
    studioConditionChanged: (state, action) => {
      studioConditionChangedState(state, action.payload);
    },
    sceneInStudioChanged: (state, action) => {
      sceneInStudioChangedState(state, action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserStudios.fulfilled, (state, action) => {
        userStudiosState(state, action.payload);
        updateStateStatus(state, 'studios', 'succeeded');
      })
      .addCase(fetchUserStudios.rejected, (state, action) => {
        updateStateStatus(state, 'studios', 'failed', action);
      })
      .addCase(fetchStudioByUuid.pending, state => {
        updateStateStatus(state, 'studio', 'loading');
      })
      .addCase(fetchStudioByUuid.fulfilled, (state, action) => {
        studioByUuidState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(fetchStudioByUuid.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(updateStudio.fulfilled, (state, action) => {
        updatedStudioState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(updateStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(deleteStudio.fulfilled, (state, action) => {
        deleteStudioState(state, action.payload);
      })
      .addCase(addEquipmentToStudio.fulfilled, (state, action) => {
        addEquipmentState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(addEquipmentToStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(updateEquipmentFromStudio.fulfilled, (state, action) => {
        updateEquipmentState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(updateEquipmentFromStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(deleteEquipmentFromStudio.fulfilled, (state, action) => {
        deleteEquipmentState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(deleteEquipmentFromStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(addSceneToStudio.fulfilled, (state, action) => {
        addSceneToStudioState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(addSceneToStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(removeSceneFromStudio.fulfilled, (state, action) => {
        deleteSceneFromStudioState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(removeSceneFromStudio.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(fetchStudiosMembers.fulfilled, (state, action) => {
        studiosMembersState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(addStudioMember.fulfilled, (state, action) => {
        addStudioMemberState(state, action.payload);
        updateStateStatus(state, 'studio', 'succeeded');
      })
      .addCase(addStudioMember.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      })
      .addCase(removeStudioMember.fulfilled, (state, action) => {
        deleteStudioMemberState(state, action.payload);
      })
      .addCase(removeStudioMember.rejected, (state, action) => {
        updateStateStatus(state, 'studio', 'failed', action);
      });
  },
});

export const {
  clearStudio,
  clearError,
  webSocketConnecting,
  webSocketConnected,
  webSocketClosed,
  webSocketError,
  webSocketMessageError,
  sceneInStudioChanged,
  studioStateChanged,
  studioConditionChanged,
} = studioSlice.actions;

export const selectStudioStatus = state => state.studio.status;

export default studioSlice.reducer;
